import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ActiveService } from 'src/app/active-service';
import { ServiceAreaModel } from 'src/app/models/service-area.model';

import { CountriesModel } from '../../../models/countries.model';
import { CustomerModel } from '../../../models/customer.model';
import { DelivererModel } from '../../../models/deliverer.model';
import { DiscountModel } from '../../../models/discount.model';
import { MembersModel } from '../../../models/members.model';
import { StatesAndCitiesModel } from '../../../models/states.model';

@Injectable()
export class MembersService extends ActiveService {
    constructor(
        public http: HttpClient,
        public store: Store
    ) {
        super(http, store);
    }

    add(obj, orgId: string): Observable<MembersModel> {
        return this.post(`organizations/${orgId}/member`, obj).pipe(
            map((resp: MembersModel) => {
                return new MembersModel(resp['models'].staff);
            }),
            catchError((error) => {
                throw error?.error?.models?.staff;
            })
        );
    }

    updateStaff(obj): Observable<MembersModel> {
        return this.put(`staff/${obj.id}`, obj).pipe(
            map((resp) => {
                return new MembersModel(resp.models.staff);
            }),
            catchError((error) => {
                throw error?.error?.models?.staff;
            })
        );
    }

    getAll(orgId: string): Observable<any> {
        return this.get(`organizations/${orgId}/members`).pipe(
            map((resp) => {
                return resp.models.staff.map((item) => new MembersModel(item));
            }),
            catchError((error) => {
                throw error?.error?.models?.staff;
            })
        );
    }

    getRoles(orgId: string): Observable<any> {
        return this.get(`roles/organization/${orgId}`).pipe(
            map((resp) => {
                if (Array.isArray(resp?.models?.role)) {
                    return resp?.models?.role;
                }
                return [];
            }),
            catchError((error) => {
                throw error?.error?.models?.role;
            })
        );
    }

    deleteStaff(obj): Observable<any> {
        return this.delete(`members/${obj.id}`).pipe(
            map((resp) => {
                return resp;
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    resetPin(memberId: string): Observable<any> {
        return this.put(`staff/${memberId}/resetpin`, memberId).pipe(
            map((resp) => {
                return new MembersModel(resp.models.staff);
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    addCustomer(obj): Observable<CustomerModel> {
        return this.post('customers', obj).pipe(
            map((resp) => {
                return new CustomerModel(resp.models.customer);
            }),
            catchError((error) => {
                throw error?.error?.models?.customer;
            })
        );
    }

    getCustomers(orgId: string): Observable<CustomerModel[]> {
        return this.get(`customers/organization/${orgId}`).pipe(
            map((resp) => {
                const customers = resp.models.customers;
                if (Array.isArray(customers)) {
                    return customers.map((item) => new CustomerModel(item));
                }
                return [] as CustomerModel[];
            }),
            catchError((error) => {
                throw error?.error?.models?.customers;
            })
        );
    }

    updateCustomer(obj): Observable<CustomerModel> {
        return this.put(`customers/${obj.id}`, obj).pipe(
            map((resp) => {
                return new CustomerModel(resp.models.customer);
            }),
            catchError((error) => {
                throw error?.error?.models?.customer;
            })
        );
    }

    deleteCustomer(obj): Observable<CustomerModel> {
        return this.delete(`customers/${obj.id}`).pipe(
            map((resp) => {
                return resp;
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    addDeliverer(obj): Observable<DelivererModel> {
        return this.post('deliverers', obj).pipe(
            map((resp) => {
                return new DelivererModel(resp.models.deliverer);
            }),
            catchError((error) => {
                throw error?.error?.models?.deliverer;
            })
        );
    }

    getDeliverers(orgId: string): Observable<DelivererModel[]> {
        return this.get(`deliverers/organization/${orgId}`).pipe(
            map((resp) => {
                const deliverers = resp.models.deliverers;
                if (Array.isArray(deliverers)) {
                    return deliverers.map((item) => new DelivererModel(item));
                }
                return [] as DelivererModel[];
            }),
            catchError((error) => {
                throw error?.error?.models?.deliverers;
            })
        );
    }

    updateDeliverer(obj): Observable<DelivererModel> {
        return this.put(`deliverers/${obj.id}`, obj).pipe(
            map((resp) => {
                return new DelivererModel(resp.models.deliverer);
            }),
            catchError((error) => {
                throw error?.error?.models?.deliverer;
            })
        );
    }

    // Delete deliverer
    deleteDeliverer(obj): Observable<DelivererModel> {
        return this.delete(`deliverers/${obj.id}`).pipe(
            map((resp) => {
                return resp;
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    getCountries(): Observable<CountriesModel[]> {
        return this.get('countries').pipe(
            map((resp) => {
                const countries = resp.models.countries;
                if (Array.isArray(countries)) {
                    return countries.map((country) => new CountriesModel(country));
                }
                return [] as CountriesModel[];
            }),
            catchError((error) => {
                throw error?.error?.models;
            })
        );
    }

    getStates(id): Observable<StatesAndCitiesModel[]> {
        return this.get(`country/states?id=${id}`).pipe(
            map((resp) => {
                const states = resp.models.states;
                if (Array.isArray(states)) {
                    return states.map((state) => new StatesAndCitiesModel(state));
                }
                return [] as StatesAndCitiesModel[];
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    getCities(id): Observable<StatesAndCitiesModel[]> {
        return this.get(`state/cities?id=${id}`).pipe(
            map((resp) => {
                const cities = resp.models.cities;
                if (Array.isArray(cities)) {
                    return cities.map((city) => new StatesAndCitiesModel(city));
                }
                return [] as StatesAndCitiesModel[];
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    addDiscount(obj): Observable<DiscountModel> {
        return this.post('discounts', obj).pipe(
            map((resp) => {
                return new DiscountModel(resp.models.discount);
            }),
            catchError((error) => {
                throw error?.error?.models?.discount;
            })
        );
    }

    getDiscounts(orgId: string): Observable<DiscountModel[]> {
        return this.get(`discounts/organization/${orgId}`).pipe(
            map((resp) => {
                const discounts = resp.models?.discounts;
                if (Array.isArray(discounts)) {
                    return discounts.map((item) => new DiscountModel(item));
                }
                return [] as DiscountModel[];
            }),
            catchError((error) => {
                throw error?.error?.models?.discounts;
            })
        );
    }

    updateDiscount(obj): Observable<DiscountModel> {
        return this.put(`discounts/${obj.id}`, obj).pipe(
            map((resp) => {
                const discounts = resp.models?.discount;
                return new DiscountModel(discounts);
            }),
            catchError((error) => {
                throw error?.error?.models?.discount;
            })
        );
    }

    deleteDiscount(obj): Observable<DiscountModel> {
        return this.delete(`discounts/${obj.id}`).pipe(
            map((resp) => {
                return resp;
            }),
            catchError((error) => {
                throw error?.error?.message;
            })
        );
    }

    getServiceAreas(orgId: string): Observable<ServiceAreaModel[]> {
        return this.get(`service-areas/organization/${orgId}`).pipe(
            map((resp) => {
                const service_area = resp?.models?.service_area;
                if (Array.isArray(service_area) && service_area.length) {
                    return service_area.map((item) => new ServiceAreaModel(item));
                }
                return [] as ServiceAreaModel[];
            }),
            catchError((error) => {
                throw error?.error?.models?.service_area;
            })
        );
    }
}
